// ui/composables/useBillingExport.js
import { trackBillingItemsExported } from '../../infrastructure/analytics/revenueCycleAnalytics';
import { markBillingItemsExported } from '../../infrastructure/api/billingExportApi';
import { describeExport, exportableRows, exportUsage } from '../presenters/billingExportPresenter';

/**
 * Recording that a file of billing items left the system.
 *
 * THE COMPOSITION ROOT for the export stamp, and the reason it exists rather than the page calling
 * the adapter itself: infrastructure is reachable from a composable and never from a page. The same
 * role `useBillingWorklist` plays for reading items, for the same rule.
 *
 * ONLY THE BILLABLE ROWS ARE STAMPED, AND THE WHOLE FILE IS DESCRIBED. A blocked row was not
 * billed, so marking it exported would hide it from the next export that should offer it — but it
 * was in the file, and an audit record that cannot say so understates what a biller received.
 *
 * IT HOLDS NO STATE. There is nothing to show between the file being written and the stamp landing:
 * the file is already downloaded either way, and the page reloads once the stamp succeeds.
 */
// eslint-disable-next-line import/prefer-default-export
export function useBillingExport() {
  /**
   * Stamp what a file contained and record that it was taken.
   *
   * `billableRows` IS NOT `stamped`, and the difference is what to tell the biller. The server
   * stamps what it can still find, so an item whose activity was deleted in the meantime comes back
   * skipped — but the row is in the file they are holding and will key, so the count they are shown
   * is what went out rather than what the server could mark.
   *
   * @param {{clinicId: string, rows: Object[]}} request - the rows the table wrote to the file
   * @returns {Promise<{stamped: number, skipped: number, exportId: string, billableRows: number}
   *   |null>} null when the file held nothing billable, which is not a failure and not an export
   */
  async function recordExport({ clinicId, rows }) {
    const stampable = exportableRows(rows);

    if (stampable.length === 0) {
      return null;
    }

    const outcome = await markBillingItemsExported({
      clinicId,
      itemIds: stampable.map((row) => row.id),
      scope: describeExport(rows),
    });

    trackBillingItemsExported({
      ...exportUsage(rows),
      clinicId,
      stamped: outcome.stamped,
      skipped: outcome.skipped,
    });

    return { ...outcome, billableRows: stampable.length };
  } // recordExport()

  return { recordExport };
} // useBillingExport()
